import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useLazyQuery } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, ArrowLeft, Download, File, Folder, FolderOpen, Lock } from 'lucide-react'
import FileTypeIcon from '../../components/file-manager/FileIcon'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import * as Query from '../../api/gql/query'
import { API_URL } from '../../utils'
import logo from '../../assets/img/logo_signin.png'

interface Folder {
  id: string
  name: string
  description: string | null
  documentCount: number
}

interface Document {
  id: string
  name: string
  description: string | null
  filename: string
  mimetype: string
  size: number
  currentRevision?: {
    id: string
    version: number
    fileId: string
    downloadUrl?: string | null
    file?: {
      id: string
      size: number
      mimetype: string
    }
  }
}

const formatFileSize = (bytes: number) => {
  if (!bytes) return '-'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

const SharedLinkPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [passwordRequired, setPasswordRequired] = useState(false)
  const [accessError, setAccessError] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null)
  const [rootFolderId, setRootFolderId] = useState<string | null>(null)

  const [accessLink, { data, loading, called }] = useLazyQuery(Query.ACCESS_DOC_SHARE_LINK, {
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      if (result?.accessDocShareLink?.error === 'Password required') {
        setPasswordRequired(true)
        setAccessError(null)
      } else if (result?.accessDocShareLink?.error) {
        setAccessError(result.accessDocShareLink.error)
      } else if (result?.accessDocShareLink?.success) {
        setAccessError(null)
        setPasswordRequired(false)
        setIsAuthenticated(true)
        if (result.accessDocShareLink.folder) {
          setRootFolderId(result.accessDocShareLink.folder.id)
        }
      }
    },
  })

  const [browseFolder, { data: browseData, loading: browseLoading }] = useLazyQuery(
    Query.SHARED_FOLDER_CONTENTS,
    {
      fetchPolicy: 'network-only',
    }
  )

  // Auto-access on first load (without password)
  React.useEffect(() => {
    if (token && !called) {
      accessLink({ variables: { token } })
    }
  }, [token, called, accessLink])

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    accessLink({ variables: { token, password } })
  }

  const handleFolderClick = (folderId: string) => {
    setCurrentFolderId(folderId)
    browseFolder({ variables: { token, folderId } })
  }

  const handleGoUp = () => {
    if (browseData?.sharedFolderContents?.parentFolderId) {
      const parentId = browseData.sharedFolderContents.parentFolderId
      if (parentId === rootFolderId) {
        setCurrentFolderId(null)
      } else {
        setCurrentFolderId(parentId)
        browseFolder({ variables: { token, folderId: parentId } })
      }
    } else {
      setCurrentFolderId(null)
    }
  }

  const result = data?.accessDocShareLink
  const browseResult = browseData?.sharedFolderContents

  // Determine which data to show
  const displayFolder = currentFolderId ? browseResult?.currentFolder : result?.folder
  const displaySubfolders: Folder[] = currentFolderId
    ? browseResult?.subfolders || []
    : result?.subfolders || []
  const displayDocuments: Document[] = currentFolderId
    ? browseResult?.documents || []
    : result?.documents || []
  const canGoUp = currentFolderId !== null

  // Bulk download goes through the share-token-scoped zip route; single files
  // use presigned downloadUrl from GraphQL. The raw /download routes are
  // auth-only and unreachable from this public page.
  const downloadableCount = displayDocuments.filter((doc) => doc.currentRevision?.fileId).length
  const zipUrl =
    downloadableCount > 0 && token
      ? `${API_URL}/download/share/${token}/zip${currentFolderId ? `?folderId=${currentFolderId}` : ''}`
      : null

  return (
    <div className="min-h-screen bg-muted py-10">
      <div className="mx-auto w-full max-w-3xl px-4">
        <div className="mb-6 flex justify-center">
          <img src={logo} alt="Urkla" className="h-20" />
        </div>

        {/* Loading state */}
        {(loading || browseLoading) && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12">
              <Spinner />
              <p className="text-muted-foreground">{t('Shared_loading')}</p>
            </CardContent>
          </Card>
        )}

        {/* Password form */}
        {!loading && passwordRequired && !result?.success && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{t('Shared_password_required')}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm text-muted-foreground">{t('Shared_password_description')}</p>
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('Shared_password_label')}</Label>
                  <Input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('Shared_password_placeholder')}
                    autoFocus
                  />
                </div>
                {result?.error && result.error !== 'Password required' && (
                  <Alert variant="destructive">
                    <AlertDescription>{result.error}</AlertDescription>
                  </Alert>
                )}
                <Button type="submit" disabled={!password}>
                  {t('Shared_access_button')}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Error state */}
        {!loading && accessError && accessError !== 'Password required' && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
              <AlertTriangle className="h-12 w-12 text-[hsl(var(--warning))]" />
              <h4 className="text-lg font-semibold">{t('Shared_error_title')}</h4>
              <p className="text-muted-foreground">
                {accessError === 'Link not found'
                  ? t('Shared_error_not_found')
                  : accessError === 'Link has expired'
                  ? t('Shared_error_expired')
                  : accessError}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Success - show folder content */}
        {!loading && !browseLoading && result?.success && result?.folder && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-2">
                  <FolderOpen className="h-5 w-5 shrink-0 text-amber-500" />
                  <CardTitle className="truncate text-lg">
                    {displayFolder?.name || result.folder.name}
                  </CardTitle>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {zipUrl && downloadableCount > 1 && (
                    <Button asChild variant="outline" size="sm">
                      <a href={zipUrl} download>
                        <Download className="h-4 w-4" />
                        {t('Shared_download_all')}
                      </a>
                    </Button>
                  )}
                  {canGoUp && (
                    <Button variant="outline" size="sm" onClick={handleGoUp}>
                      <ArrowLeft className="h-4 w-4" />
                      {t('Shared_go_back')}
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {displayFolder?.description && (
                <div className="border-b px-6 py-3">
                  <p className="text-sm text-muted-foreground">{displayFolder.description}</p>
                </div>
              )}

              {displaySubfolders.length === 0 && displayDocuments.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-muted-foreground">
                  <Folder className="h-10 w-10 opacity-50" />
                  <p>{t('Shared_folder_empty')}</p>
                </div>
              ) : (
                <Table>
                  <TableBody>
                    {/* Subfolders */}
                    {displaySubfolders.map((folder: Folder) => (
                      <TableRow
                        key={`folder-${folder.id}`}
                        onClick={() => handleFolderClick(folder.id)}
                        className="cursor-pointer"
                      >
                        <TableCell className="py-3">
                          <div className="flex items-center gap-3">
                            <Folder className="h-5 w-5 shrink-0 text-amber-500" />
                            <div className="min-w-0">
                              <div className="font-medium">{folder.name}</div>
                              {folder.description && (
                                <p className="text-sm text-muted-foreground">{folder.description}</p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="w-[120px] py-3 text-right">
                          {folder.documentCount > 0 && (
                            <Badge variant="secondary" className="rounded-full">
                              {folder.documentCount} {t('Shared_files')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="w-[100px]" />
                      </TableRow>
                    ))}

                    {/* Documents */}
                    {displayDocuments.map((doc: Document) => {
                      return (
                        <TableRow key={`doc-${doc.id}`}>
                          <TableCell className="py-3">
                            <div className="flex items-center gap-3">
                              <FileTypeIcon filename={doc.filename} size={20} />
                              <div className="min-w-0">
                                <div className="font-medium">{doc.name}</div>
                                {doc.description && (
                                  <p className="text-sm text-muted-foreground">{doc.description}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="w-[120px] py-3 text-right">
                            <span className="text-sm text-muted-foreground">
                              {formatFileSize(doc.currentRevision?.file?.size || doc.size)}
                            </span>
                            {doc.currentRevision && (
                              <Badge variant="default" className="ml-2 rounded-full">
                                v{doc.currentRevision.version}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="w-[100px] py-3 text-right">
                            {doc.currentRevision?.downloadUrl && (
                              <Button asChild size="sm">
                                <a
                                  href={doc.currentRevision.downloadUrl}
                                  download={doc.filename}
                                >
                                  <Download className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        )}

        {/* Success - show document */}
        {!loading && result?.success && result?.document && (
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <File className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg">{result.document.name}</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {result.document.description && (
                <p className="text-muted-foreground">{result.document.description}</p>
              )}

              <div className="flex items-center justify-between gap-4 rounded-md bg-muted p-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{result.document.filename}</div>
                  <p className="text-sm text-muted-foreground">
                    {formatFileSize(result.document.size)}
                    {result.document.currentRevision && (
                      <> &middot; v{result.document.currentRevision.version}</>
                    )}
                  </p>
                </div>
                {result.document.currentRevision?.downloadUrl && (
                  <Button asChild>
                    <a
                      href={result.document.currentRevision.downloadUrl}
                      download={result.document.filename}
                    >
                      <Download className="h-4 w-4" />
                      {t('Shared_download_button')}
                    </a>
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t('Shared_footer_text')}
        </p>
      </div>
    </div>
  )
}

export default SharedLinkPage
